import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'analysis-walkthrough',
    category: 'analysis',
    title: 'Dal caricamento al risultato — una procedura guidata di 15 minuti',
    summary: 'Un\'esecuzione completa dall\'inizio alla fine: crea una comunità, carica un file, ottieni una previsione e un primo risultato di ottimizzazione.',
    body: (
      <>
        <p>
          Questo articolo mette tutto insieme. Seguilo una volta e avrai utilizzato ogni strumento
          almeno una volta. Presuppone che tu abbia pronto il file di consumo orario di un singolo edificio.
        </p>

        <ol>
          <li>
            <strong>Crea una comunità.</strong> Pannello → <em>Comunità</em> →
            {' '}<em>Nuova comunità</em>. Assegna un nome e scegli il paese e la valuta. Salva.
          </li>
          <li>
            <strong>Aggiungi almeno un edificio.</strong> Apri la comunità → <em>Edifici</em> →
            {' '}<em>Aggiungi edificio</em>. Il nome dell'edificio, il tipo e un codice postale sono sufficienti per iniziare.
          </li>
          <li>
            <strong>Carica il tuo file di carico.</strong> Comunità → <em>Gestione Dati</em> → seleziona
            {' '}<em>Building Load Profile</em>, scegli l'edificio, trascina il CSV. Consulta
            {' '}<ArticleLink to="data-upload-how">Caricare un file, passo per passo</ArticleLink> se
            la pagina non ti è familiare.
          </li>
          <li>
            <strong>(Consigliato) Carica un file tariffario.</strong> Senza prezzi, i risultati sui costi saranno
            solo indicativi. Consulta <ArticleLink to="data-tariff">File tariffari</ArticleLink>.
          </li>
          <li>
            <strong>Esegui una previsione.</strong> Barra laterale della comunità → <em>Previsione</em>. La pagina ha
            due schede — <em>Breve termine (Day-Ahead)</em> e <em>Lungo termine (mensile)</em>. Seleziona un
            edificio, clicca <em>Esegui previsione</em>. Consulta
            {' '}<ArticleLink to="analysis-forecast">Eseguire una previsione</ArticleLink>.
          </li>
          <li>
            <strong>Esegui un'ottimizzazione.</strong> Barra laterale della comunità → <em>Ottimizzazione</em>.
            Scegli un obiettivo (ad es. <em>Minimizza il costo</em>)
            e clicca <em>Esegui ottimizzazione Day-Ahead</em>. Consulta
            {' '}<ArticleLink to="analysis-optimisation">Eseguire un'ottimizzazione</ArticleLink>.
          </li>
          <li>
            <strong>Leggi i risultati.</strong> Numeri, grafici e risparmi appaiono nella stessa pagina.
            Il significato di ciascun KPI si trova nel <ArticleLink to="results-glossary">glossario</ArticleLink>;
            come interpretare ciascun grafico è spiegato nel resto di questa sezione.
          </li>
        </ol>

        <Tip>
          La piattaforma mostra risultati illustrativi chiaramente etichettati se un servizio non è disponibile o
          i dati richiesti non sono stati caricati. Se un risultato appare sospettosamente tondo (ad es. esattamente
          100 €/anno), controlla la pagina per un avviso «dati di esempio».
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-forecast',
    category: 'analysis',
    title: 'Eseguire una previsione',
    summary: 'Prevedi le prossime ore/giorni di consumo o generazione di un edificio o di una comunità.',
    body: (
      <>
        <p>
          Una <strong>previsione</strong> utilizza i tuoi dati storici per predire il carico o la generazione futura.
          Usala per pianificare le operazioni, per guidare un'ottimizzazione Day-Ahead, o semplicemente per vedere
          come si presenterà domani — o il prossimo trimestre.
        </p>

        <h5 className="mt-3">Prima di eseguirla</h5>
        <ul>
          <li>La comunità ha almeno un edificio con un <strong>Building Load Profile</strong>
            caricato (più storico → migliore previsione — si consiglia almeno 3 mesi di dati per
            previsioni affidabili, idealmente un anno).</li>
          <li>Per previsioni <em>solari</em> o <em>eoliche</em>, hai bisogno anche di un file di generazione (profilo
            FV/eolico della comunità o il solare sul tetto dell'edificio). Un
            {' '}<ArticleLink to="data-weather">file meteo</ArticleLink> è facoltativo ma migliora
            la precisione del 15–30%.</li>
        </ul>

        <h5 className="mt-3">Passo per passo</h5>
        <ol>
          <li>Apri la comunità → scegli <strong>Previsione</strong> nella barra laterale.</li>
          <li>Seleziona la <strong>modalità</strong> dalle due schede in cima alla pagina:
            <ul>
              <li><strong>Breve termine (Day-Ahead)</strong> — prevede le prossime 24 ore a partire da una data
                target scelta (fino a 7 giorni avanti).</li>
              <li><strong>Lungo termine (mensile)</strong> — proietta i dati mensili su un orizzonte che
                scegli: <em>3 mesi</em>, <em>6 mesi</em> o <em>12 mesi</em>.</li>
            </ul>
          </li>
          <li>Seleziona gli <strong>asset</strong> da prevedere: il carico di un edificio, la
            generazione FV della comunità o la generazione eolica della comunità. Puoi selezionarne più di uno.</li>
          <li>Clicca <strong>Esegui previsione</strong> (l'etichetta del pulsante si adatta al numero di asset
            selezionati). Un indicatore mostra <em>Previsione in corso…</em> durante l'elaborazione.</li>
          <li>I risultati appaiono nel pannello a destra. Consulta
            {' '}<ArticleLink to="results-forecast">Leggere il grafico delle previsioni</ArticleLink>
            {' '}per il significato di ciascuna linea e banda.</li>
        </ol>
        <ScreenshotPlaceholder
          img="forecast-page.png"
          description="The Forecasting page — the Short-Term / Long-Term tabs, the asset selector, and the Run Forecast button on the left; the result panel placeholder on the right."
          source="Platform — open any community → click 'Forecasting' in the sidebar. Capture the page before running anything, with both tabs visible."
          caption="La pagina Previsioni: scegli una scheda, seleziona gli asset, avvia."
        />

        <Tip>
          Se la stessa previsione appare molto diversa da un'esecuzione all'altra, probabilmente disponi di
          pochissimi dati di input. Aggiungi più storico e riesegui. Consulta
          {' '}<ArticleLink to="tb-forecast-flat">«La mia previsione appare piatta o errata»</ArticleLink>
          {' '}nella sezione Risoluzione dei problemi se i risultati sembrano ancora anomali.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'analysis-optimisation',
    category: 'analysis',
    title: 'Eseguire un\'ottimizzazione (Ottimizzazione)',
    summary: 'Decidi quando importare, esportare, caricare o scaricare — per minimizzare il costo o massimizzare l\'autosufficienza.',
    body: (
      <>
        <p>
          Lo strumento <strong>Ottimizzazione</strong> trova il programma migliore per gli asset flessibili
          della tua comunità — quando caricare o scaricare una batteria, quando caricare i veicoli elettrici, quando
          importare dalla rete e quando esportare — in base all'obiettivo che scegli.
        </p>

        <h5 className="mt-3">Prima di eseguirla</h5>
        <ul>
          <li>Una comunità con almeno un file di <strong>carico</strong> caricato.</li>
          <li>Un file <strong>tariffario</strong> (l'ottimizzatore ha bisogno dei prezzi per sapere cosa è economico e
            cosa è costoso). Consulta <ArticleLink to="data-tariff">File tariffari</ArticleLink>.</li>
          <li>Facoltativo ma consigliato: un file di <strong>generazione</strong> e qualsiasi
            {' '}<strong>asset</strong> (FV, batteria, EV) inserito per la comunità.</li>
        </ul>

        <h5 className="mt-3">Passo per passo</h5>
        <ol>
          <li>Apri la comunità → scegli <strong>Ottimizzazione</strong> nella barra laterale.</li>
          <li>Scegli un <strong>obiettivo</strong>:
            <ul>
              <li><em>Minimizza il costo</em> — acquista quando i prezzi sono bassi, vendi quando sono alti.
                Predefinito per la maggior parte delle comunità.</li>
              <li><em>Massimizza l'autosufficienza</em> — usa quanta più generazione propria possibile,
                anche se il costo è leggermente superiore.</li>
              <li><em>Massimizza l'autoconsumo</em> — minimizza le esportazioni della tua generazione.</li>
            </ul>
          </li>
          <li>L'ottimizzazione esegue un programma di un giorno (24 ore) per il giorno successivo.</li>
          <li>Verifica l'elenco degli asset (FV, batteria, caricatori EV). Le impostazioni della batteria come
            {' '}<em>stato di carica minimo/massimo</em> e <em>efficienza di andata e ritorno</em> devono
            corrispondere alla realtà — modificale se necessario.</li>
          <li>Clicca <strong>Esegui ottimizzazione Day-Ahead</strong>. Un indicatore mostra <em>Ottimizzazione in corso…</em>
            (potresti anche vedere un messaggio «Solving the MILP problem» — è il risolutore matematico
            al lavoro). Un'esecuzione su pochi edifici di solito termina in meno di un minuto.</li>
          <li>Al termine, il pannello dei risultati appare in basso — schede KPI in alto, grafici orari del
            Bilancio Energetico e della batteria in basso. Consulta
            {' '}<ArticleLink to="results-optimisation-kpis">Leggere i KPI dell'ottimizzazione</ArticleLink>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="modelling-page.png"
          description="The Modelling page (reached via the Optimization sidebar item) — objective selector (Minimise cost / Maximise self-sufficiency / Maximise self-consumption), the asset list, and the Run Optimization button."
          source="Platform — open any community → click 'Optimization' in the sidebar. (The page is titled 'Modelling'.) Capture before running, showing the objective radios and the asset list."
          caption="La pagina Ottimizzazione: scegli un obiettivo, verifica gli asset, avvia."
        />

        <Warn>
          Se l'esecuzione termina con «infeasible», i vincoli non possono essere tutti soddisfatti contemporaneamente
          (ad esempio, la batteria è troppo piccola per la domanda). Consulta
          {' '}<ArticleLink to="tb-optimisation-infeasible">«L'ottimizzazione dice infeasible»</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
